/**
 * ErrorMessage Component
 * 
 * User-friendly error display with retry capabilities.
 * Translates technical errors into understandable messages.
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faExclamationCircle,
  faWifi,
  faLock,
  faServer,
  faSync,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import './ErrorMessage.css';

// Error type definitions with user-friendly messages
const ERROR_TYPES = {
  network: {
    icon: faWifi,
    title: 'Connection Lost',
    message: "Arr! We've lost connection to the seas. Check yer internet and try again.",
    color: 'warning'
  },
  auth: {
    icon: faLock,
    title: 'Join the Guild to continue',
    message: "Sign up free as a Deckhand to unlock this — no card, no commitment. Already aboard? Sign in to keep sailing.",
    color: 'warning'
  },
  permission: {
    icon: faLock,
    title: 'Permission Denied',
    message: "Ye don't have the proper papers for this voyage. Contact the captain if ye think this be an error.",
    color: 'error'
  },
  notFound: {
    icon: faExclamationTriangle,
    title: 'Not Found',
    message: "This treasure doesn't exist or has been moved to another island.",
    color: 'warning'
  },
  server: {
    icon: faServer,
    title: 'Server Error',
    message: "Our ship is experiencing technical difficulties. The crew is working on it!",
    color: 'error'
  },
  validation: {
    icon: faExclamationCircle,
    title: 'Invalid Data',
    message: "Something's not quite right with the information provided.",
    color: 'warning'
  },
  timeout: {
    icon: faWifi,
    title: 'Request Timeout',
    message: "The server took too long to respond. Please try again.",
    color: 'warning'
  },
  unknown: {
    icon: faExclamationTriangle,
    title: 'Something Went Wrong',
    message: "Shiver me timbers! An unexpected error occurred. Please try again.",
    color: 'error'
  }
};

/**
 * Determine error type from error object or code
 */
const getErrorType = (error, type) => {
  if (type && ERROR_TYPES[type]) {
    return type;
  }

  if (!error) return 'unknown';

  const message = error.message?.toLowerCase() || '';
  const code = error.code?.toLowerCase() || '';

  // Network errors
  if (
    message.includes('network') ||
    message.includes('failed to fetch') ||
    code.includes('network') ||
    !navigator.onLine
  ) {
    return 'network';
  }

  // Auth errors
  if (
    code.includes('auth') ||
    code.includes('unauthenticated') ||
    message.includes('not authenticated')
  ) {
    return 'auth';
  }

  // Permission errors
  if (
    code.includes('permission') ||
    code.includes('forbidden') ||
    code.includes('unauthorized') ||
    message.includes('permission denied')
  ) {
    return 'permission';
  }

  // Not found errors
  if (
    code.includes('not-found') ||
    code.includes('404') ||
    message.includes('not found')
  ) {
    return 'notFound';
  }

  // Validation errors
  if (
    code.includes('invalid') ||
    code.includes('validation') ||
    message.includes('invalid')
  ) {
    return 'validation';
  }

  // Timeout errors
  if (
    code.includes('timeout') ||
    message.includes('timeout') ||
    message.includes('timed out')
  ) {
    return 'timeout';
  }

  // Server errors
  if (
    code.includes('internal') ||
    code.includes('server') ||
    message.includes('server error')
  ) {
    return 'server';
  }

  return 'unknown';
};

/**
 * ErrorMessage Component
 */
const ErrorMessage = ({
  error,
  type,
  title,
  message,
  onRetry,
  showHomeButton = false,
  showDetails = false,
  className = '',
  size = 'medium', // 'small', 'medium', 'large'
  inline = false
}) => {
  const navigate = useNavigate();
  const errorType = getErrorType(error, type);
  const errorConfig = ERROR_TYPES[errorType];

  const displayTitle = title || errorConfig.title;
  const displayMessage = message || errorConfig.message;

  const handleGoHome = () => {
    navigate('/');
  };

  if (inline) {
    return (
      <div className={`error-message-inline error-${errorConfig.color} ${className}`}>
        <FontAwesomeIcon icon={errorConfig.icon} className="error-inline-icon" />
        <span className="error-inline-text">{displayMessage}</span>
        {onRetry && (
          <button className="error-inline-retry" onClick={onRetry}>
            <FontAwesomeIcon icon={faSync} /> Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`error-message error-message-${size} error-${errorConfig.color} ${className}`}>
      <div className="error-message-icon">
        <FontAwesomeIcon icon={errorConfig.icon} />
      </div>

      <h3 className="error-message-title">{displayTitle}</h3>
      <p className="error-message-text">{displayMessage}</p>

      {showDetails && error && (
        <div className="error-message-details">
          <code>
            {error.code && <span className="error-code">{error.code}</span>}
            {error.message && <span className="error-detail">{error.message}</span>}
          </code>
        </div>
      )}

      <div className="error-message-actions">
        {onRetry && (
          <button className="error-btn primary" onClick={onRetry}>
            <FontAwesomeIcon icon={faSync} />
            Try Again
          </button>
        )}
        {showHomeButton && (
          <button className="error-btn secondary" onClick={handleGoHome}>
            <FontAwesomeIcon icon={faHome} />
            Go Home
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Wrapper component for handling async data fetching errors
 */
export const ErrorMessageBoundary = ({ error, children, onRetry, ...props }) => {
  if (error) {
    return <ErrorMessage error={error} onRetry={onRetry} {...props} />;
  }
  return children;
};

/**
 * Hook for managing error state
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);

  const handleError = React.useCallback((err) => {
    setError(err);
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  const withErrorHandling = React.useCallback(async (asyncFn) => {
    try {
      clearError();
      return await asyncFn();
    } catch (err) {
      handleError(err);
      throw err;
    }
  }, [clearError, handleError]);

  return {
    error,
    setError: handleError,
    clearError,
    withErrorHandling
  };
};

export default ErrorMessage;
